import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import type { BillingPlanFull } from '@/api/admin'
import { formatDate, formatDateTime, formatCredits, statusColor, cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Org {
  id: string
  name: string
  slug: string
  wallet_balance_credits: number
  created_at: string
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminOrganizations() {
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: adminApi.listOrgs,
  })

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Organizations</h1>
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Organization</th>
                <th className="text-left p-3 font-medium">Slug</th>
                <th className="text-left p-3 font-medium">Wallet</th>
                <th className="text-left p-3 font-medium">Created</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org: Org) => (
                <tr key={org.id} className="border-t hover:bg-muted/20">
                  <td className="p-3 font-medium">{org.name}</td>
                  <td className="p-3 text-muted-foreground">{org.slug}</td>
                  <td className="p-3">{formatCredits(org.wallet_balance_credits)} credits</td>
                  <td className="p-3 text-muted-foreground">{formatDate(org.created_at)}</td>
                  <td className="p-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOrgId(org.id)}
                    >
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedOrgId && (
        <OrgDetailDialog
          orgId={selectedOrgId}
          orgName={orgs.find((o: Org) => o.id === selectedOrgId)?.name ?? ''}
          open={!!selectedOrgId}
          onClose={() => setSelectedOrgId(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Org Detail Dialog
// ---------------------------------------------------------------------------

function OrgDetailDialog({
  orgId,
  orgName,
  open,
  onClose,
}: {
  orgId: string
  orgName: string
  open: boolean
  onClose: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{orgName}</DialogTitle>
          <DialogDescription>Manage subscription, wallet, and view usage for this organization.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="subscription" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="wallet">Wallet</TabsTrigger>
            <TabsTrigger value="usage">Usage</TabsTrigger>
          </TabsList>

          <TabsContent value="subscription">
            <SubscriptionTab orgId={orgId} />
          </TabsContent>
          <TabsContent value="wallet">
            <WalletTab orgId={orgId} />
          </TabsContent>
          <TabsContent value="usage">
            <UsageTab orgId={orgId} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Feature 1: Subscription Tab
// ---------------------------------------------------------------------------

function SubscriptionTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()

  const { data: sub, isLoading: subLoading } = useQuery({
    queryKey: ['admin-org-subscription', orgId],
    queryFn: () => adminApi.getOrgSubscription(orgId),
  })

  const { data: plans = [] } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.listPlans,
  })

  const [planId, setPlanId] = useState('')
  const [status, setStatus] = useState('active')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [initialized, setInitialized] = useState(false)

  // Sync form state when subscription data loads
  if (sub && !initialized) {
    if (sub.plan_id) setPlanId(sub.plan_id)
    if (sub.status) setStatus(sub.status)
    if (sub.current_period_start) setPeriodStart(sub.current_period_start.slice(0, 10))
    if (sub.current_period_end) setPeriodEnd(sub.current_period_end.slice(0, 10))
    setInitialized(true)
  }

  const assignMutation = useMutation({
    mutationFn: (data: { plan_id: string; status: string; current_period_start?: string; current_period_end?: string }) =>
      adminApi.assignOrgSubscription(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-subscription', orgId] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { plan_id?: string; status?: string; current_period_start?: string; current_period_end?: string }) =>
      adminApi.updateOrgSubscription(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-subscription', orgId] })
    },
  })

  const handleSave = () => {
    if (!planId) return

    const payload: Record<string, string> = {
      plan_id: planId,
      status,
    }
    if (periodStart) payload.current_period_start = new Date(periodStart).toISOString()
    if (periodEnd) payload.current_period_end = new Date(periodEnd).toISOString()

    const hasSub = sub?.plan_id != null
    if (hasSub) {
      updateMutation.mutate(payload)
    } else {
      assignMutation.mutate(payload as { plan_id: string; status: string })
    }
  }

  const saving = assignMutation.isPending || updateMutation.isPending
  const error = assignMutation.error || updateMutation.error
  const success = assignMutation.isSuccess || updateMutation.isSuccess

  if (subLoading) {
    return <div className="py-6 text-center text-muted-foreground">Loading subscription...</div>
  }

  const hasSub = sub?.plan_id != null

  return (
    <div className="space-y-6 pt-4">
      {/* Current status */}
      {hasSub ? (
        <div className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Current Plan</span>
            <Badge className={statusColor(sub.status ?? '')}>{sub.status}</Badge>
          </div>
          <p className="text-lg font-semibold">{sub.plan_name ?? 'Unknown'}</p>
          {sub.current_period_start && (
            <p className="text-xs text-muted-foreground">
              Period: {formatDate(sub.current_period_start)} - {formatDate(sub.current_period_end)}
            </p>
          )}
          {sub.stripe_subscription_id && (
            <p className="text-xs text-muted-foreground">
              Stripe ID: {sub.stripe_subscription_id}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-4 text-center text-muted-foreground">
          No subscription assigned to this organization.
        </div>
      )}

      {/* Form */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Plan</Label>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((p: BillingPlanFull) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} {p.is_hidden ? '(hidden)' : ''} - ${(p.price_cents / 100).toFixed(2)}/{p.billing_interval ?? 'month'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="past_due">Past Due</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
              <SelectItem value="trialing">Trialing</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Period Start</Label>
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Period End</Label>
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive">
            Failed to save: {(error as Error).message}
          </p>
        )}
        {success && (
          <p className="text-sm text-green-600">Subscription saved successfully.</p>
        )}

        <Button onClick={handleSave} disabled={!planId || saving}>
          {saving ? 'Saving...' : hasSub ? 'Update Subscription' : 'Assign Plan'}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Feature 2: Wallet Tab
// ---------------------------------------------------------------------------

function WalletTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient()

  const { data: wallet, isLoading } = useQuery({
    queryKey: ['admin-org-wallet', orgId],
    queryFn: () => adminApi.getOrgWallet(orgId),
  })

  const [amount, setAmount] = useState('')
  const [txnType, setTxnType] = useState('topup')
  const [description, setDescription] = useState('')

  const adjustMutation = useMutation({
    mutationFn: (data: { amount_credits: number; type: string; description: string }) =>
      adminApi.adjustWallet(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-org-wallet', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      setAmount('')
      setDescription('')
    },
  })

  const handleSubmit = () => {
    // Credits-native since migration 015 — admin enters credits directly.
    const credits = Math.round(parseFloat(amount))
    if (isNaN(credits) || credits <= 0) return
    adjustMutation.mutate({
      amount_credits: credits,
      type: txnType,
      description: description || `Admin ${txnType}`,
    })
  }

  if (isLoading) {
    return <div className="py-6 text-center text-muted-foreground">Loading wallet...</div>
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Balance */}
      <div className="rounded-lg border p-4">
        <p className="text-sm text-muted-foreground">Current Balance</p>
        <p className="text-3xl font-bold">{formatCredits(wallet?.balance_credits ?? 0)} credits</p>
      </div>

      {/* Adjust form */}
      <div className="rounded-lg border p-4 space-y-4">
        <h3 className="font-medium">Adjust Balance</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Amount (credits)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={txnType} onValueChange={setTxnType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="topup">Add Credit</SelectItem>
                <SelectItem value="deduction">Deduct</SelectItem>
                <SelectItem value="refund">Refund</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            placeholder="Reason for adjustment..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        {adjustMutation.error && (
          <p className="text-sm text-destructive">
            Failed: {(adjustMutation.error as Error).message}
          </p>
        )}
        {adjustMutation.isSuccess && (
          <p className="text-sm text-green-600">Balance adjusted successfully.</p>
        )}

        <Button
          onClick={handleSubmit}
          disabled={!amount || parseFloat(amount) <= 0 || adjustMutation.isPending}
        >
          {adjustMutation.isPending ? 'Adjusting...' : 'Submit Adjustment'}
        </Button>
      </div>

      {/* Recent transactions */}
      <div className="space-y-2">
        <h3 className="font-medium">Recent Transactions</h3>
        {wallet?.transactions && wallet.transactions.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium">Date</th>
                  <th className="text-left p-2 font-medium">Type</th>
                  <th className="text-right p-2 font-medium">Amount</th>
                  <th className="text-right p-2 font-medium">Balance After</th>
                  <th className="text-left p-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {wallet.transactions.map((txn) => (
                  <tr key={txn.id} className="border-t">
                    <td className="p-2 text-muted-foreground">{formatDateTime(txn.created_at)}</td>
                    <td className="p-2">
                      <Badge
                        variant={txn.type === 'topup' ? 'success' : txn.type === 'refund' ? 'info' : 'destructive'}
                      >
                        {txn.type}
                      </Badge>
                    </td>
                    <td className={cn(
                      'p-2 text-right font-mono',
                      txn.type === 'deduction' ? 'text-red-600' : 'text-green-600'
                    )}>
                      {txn.type === 'deduction' ? '-' : '+'}{formatCredits(txn.amount_credits)}
                    </td>
                    <td className="p-2 text-right font-mono">{formatCredits(txn.balance_after_credits)}</td>
                    <td className="p-2 text-muted-foreground text-xs">{txn.description ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No transactions yet.</p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Feature 3: Usage Tab
// ---------------------------------------------------------------------------

function UsageTab({ orgId }: { orgId: string }) {
  const [months, setMonths] = useState(6)

  const { data: usage = [], isLoading } = useQuery({
    queryKey: ['admin-org-usage', orgId, months],
    queryFn: () => adminApi.getOrgUsage(orgId, months),
  })

  if (isLoading) {
    return <div className="py-6 text-center text-muted-foreground">Loading usage data...</div>
  }

  // Calculate totals
  const totals = usage.reduce(
    (acc, row) => ({
      documents_processed: acc.documents_processed + row.documents_processed,
      extraction_runs: acc.extraction_runs + row.extraction_runs,
      tokens_used: acc.tokens_used + row.tokens_used,
      credits_consumed: acc.credits_consumed + row.credits_consumed,
      credits_added: acc.credits_added + row.credits_added,
    }),
    { documents_processed: 0, extraction_runs: 0, tokens_used: 0, credits_consumed: 0, credits_added: 0 }
  )

  const currentMonth = new Date().toISOString().slice(0, 7)

  return (
    <div className="space-y-4 pt-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Monthly Breakdown</h3>
        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Storage info */}
      {usage.length > 0 && (
        <div className="rounded-lg border p-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Current Storage Usage</span>
          <span className="font-medium">{usage[0].storage_used_mb.toFixed(1)} MB</span>
        </div>
      )}

      {/* Table */}
      {usage.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 font-medium">Month</th>
                <th className="text-right p-2 font-medium">Docs Processed</th>
                <th className="text-right p-2 font-medium">Runs</th>
                <th className="text-right p-2 font-medium">Tokens</th>
                <th className="text-right p-2 font-medium">Credits Used</th>
                <th className="text-right p-2 font-medium">Credits Added</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((row) => (
                <tr
                  key={row.month}
                  className={cn(
                    'border-t',
                    row.month === currentMonth && 'bg-blue-50/50'
                  )}
                >
                  <td className="p-2 font-medium">
                    {row.month}
                    {row.month === currentMonth && (
                      <Badge variant="info" className="ml-2 text-[10px]">current</Badge>
                    )}
                  </td>
                  <td className="p-2 text-right font-mono">{row.documents_processed.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono">{row.extraction_runs.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono">{row.tokens_used.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono">{row.credits_consumed.toFixed(2)}</td>
                  <td className="p-2 text-right font-mono">{row.credits_added.toFixed(2)}</td>
                </tr>
              ))}

              {/* Totals row */}
              <tr className="border-t-2 bg-muted/30 font-semibold">
                <td className="p-2">Totals</td>
                <td className="p-2 text-right font-mono">{totals.documents_processed.toLocaleString()}</td>
                <td className="p-2 text-right font-mono">{totals.extraction_runs.toLocaleString()}</td>
                <td className="p-2 text-right font-mono">{totals.tokens_used.toLocaleString()}</td>
                <td className="p-2 text-right font-mono">{totals.credits_consumed.toFixed(2)}</td>
                <td className="p-2 text-right font-mono">{totals.credits_added.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center py-6 text-muted-foreground">No usage data available.</p>
      )}
    </div>
  )
}
